import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchYdelser, fetchVirksomhedsoplysninger } from '../lib/cms';
import ProjectAlbums from '../components/ProjectAlbums';

export default function Home() {
  const [page, setPage] = useState(null);
  const [kontaktPage, setKontaktPage] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ydelser med billeder og links (defaults / CMS fallback)
  const defaultServices = [
    {
      title: 'Bildekoration & Indpakning',
      description: 'Gør firmabilen til dit stærkeste visitkort med holdbar foliedekoration og professionel helindpakning.',
      image: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-7762.jpeg',
      href: '/dekoration/bil',
      icon: 'fa-solid fa-car'
    },
    {
      title: 'Solfilm til biler & maskiner',
      description: 'Reducer varme, blænding og UV-stråler i kabinen. Vi monterer professionelt med op til 7 års garanti.',
      image: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/stc-car-solfilm.jpg',
      href: '/solfilm/bil',
      icon: 'fa-solid fa-car-side'
    },
    {
      title: 'Interiør folie',
      description: 'Giv dine møbler, køkkener, døre og inventar nyt liv med slidstærk kvalitetsfolie i mange designs.',
      image: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-4357.jpeg',
      href: '/interioerfolie',
      icon: 'fa-solid fa-couch'
    },
    {
      title: 'Lastbildekoration',
      description: 'Få maksimal synlighed på vejene med slidstærke reklamer til lastbiler, trækker og trailere.',
      image: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-9103.jpeg',
      href: '/dekoration/lastbil',
      icon: 'fa-solid fa-truck'
    },
    {
      title: 'Vægfolie & Indendørs Grafik',
      description: 'Skab inspirerende lokaler med grafisk vægdekoration, motiver og firmaprofilering.',
      image: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-1963.jpeg',
      href: '/dekoration/vaeg',
      icon: 'fa-solid fa-paintbrush'
    },
    {
      title: 'Logo design & Grafisk',
      description: 'Vi skaber professionelle logoer og tryksager, der styrker dit firmas visuelle identitet.',
      image: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/female-logo-designer-working-graphic-tablet.jpeg',
      href: '/logodesign',
      icon: 'fa-solid fa-pen-nib'
    }
  ];

  const [cmsServices, setCmsServices] = useState(defaultServices);

  useEffect(() => {
    async function loadData() {
      const [pageData, kontaktData, ydelserData, companyData] = await Promise.all([
        fetchPageBySlug('index'),
        fetchPageBySlug('kontakt'),
        fetchYdelser(),
        fetchVirksomhedsoplysninger()
      ]);
      setPage(pageData);
      setKontaktPage(kontaktData);
      setCompanyInfo(companyData);
      if (ydelserData && ydelserData.length > 0) {
        const formatted = ydelserData.map((y) => ({
          title: y.titel || y.title,
          description: y.beskrivelse || y.description,
          image: (y.billede && y.billede[0]) || (y.image && y.image[0]) || y.billede || '',
          href: y.link || y.href || '/',
          icon: y.ikon || y.icon || 'fa-solid fa-check'
        }));
        setCmsServices(formatted);
      }
      setLoading(false);
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const services = cmsServices;
  
  // Hero-billedet (køkken/hovedbillede i det grønne felt) redigeres direkte i CMS under 'sider' (side_slug: 'index') i feltet 'hero_billede' (eller 'billeder')
  const rawHeroImage =
    (page?.hero_billede && (Array.isArray(page.hero_billede) ? page.hero_billede[0] : page.hero_billede)) ||
    (page?.hero_image && (Array.isArray(page.hero_image) ? page.hero_image[0] : page.hero_image)) ||
    (page?.billeder && page.billeder[0]) ||
    (page?.images && page.images[0]) ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-4357.jpeg';

  const heroImage = typeof rawHeroImage === 'string' ? rawHeroImage : (rawHeroImage?.[0] || '');

  const heroImageAlt =
    page?.hero_billede_alt ||
    page?.hero_image_alt ||
    page?.overskrift ||
    page?.headline ||
    'CRE8IT - Vi bringer dine idéer til virkelighed';

  // Billede af Eveline til sektionen "Mød Eveline" (prioriterer feltet om_billede fra Forside i CMS)
  const rawOmImage =
    (page?.om_billede && (Array.isArray(page.om_billede) ? page.om_billede[0] : page.om_billede)) ||
    (kontaktPage?.billeder && kontaktPage.billeder[0]) ||
    (kontaktPage?.images && kontaktPage.images[0]) ||
    (page?.billeder && page.billeder[1]) ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/6a441e17-02fd-450e-af7a-99d221bff4ab_78463f7a.png';

  const omImage = typeof rawOmImage === 'string' ? rawOmImage : (rawOmImage?.[0] || '');

  const companyAdresse = companyInfo?.adresse || 'Nordre Industrivej 2a, 6630 Rødding';
  const companyPhone = companyInfo?.telefon || '7060 4788';
  const companyEmail = companyInfo?.email || 'kontor@cre8it.nu';

  return (
    <div className="pb-20 bg-slate-50/50">
      <Helmet>
        <title>{page?.meta_title || 'CRE8IT - Skilte, Interiørfolie, Bildekoration & Solfilm'}</title>
        <meta
          name="description"
          content={
            page?.meta_description ||
            'Velkommen til CRE8IT - Din professionelle partner inden for skilte, interiørfolie, bildekoration, solfilm og logo design.'
          }
        />
      </Helmet>

      {/* 1. Hero Sektion (Det grønne felt - Låst op til CMS) */}
      <section className="relative overflow-hidden bg-[#325d52] py-16 lg:py-24 border-b border-[#245b53]/40 text-white">
        {/* Subtile dekorative lysglød */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-96 w-96 rounded-full bg-[#deecd4]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Venstre kolonne: Tekst (Dynamisk fra CMS) */}
            <div className="space-y-6 lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#deecd4] px-3.5 py-1.5 text-xs font-bold text-[#325d52] border border-[#deecd4] shadow-xs">
                <span className="h-2 w-2 rounded-full bg-brand-red animate-pulse" />
                Over 15 års erfaring i skilte- & foliefaget
              </div>

              {/* Dynamisk Overskrift */}
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.15]">
                {page?.overskrift ? (
                  page.overskrift.toLowerCase().includes('idéer') || page.overskrift.toLowerCase().includes('ideer') ? (
                    <>
                      {page.overskrift.split(/(idéer|ideer)/i)[0]}
                      <span className="text-[#deecd4]">{page.overskrift.match(/(idéer|ideer)/i)?.[0] || 'idéer'}</span>
                      {page.overskrift.split(/(idéer|ideer)/i)[2] || ''}
                    </>
                  ) : (
                    page.overskrift
                  )
                ) : (
                  <>
                    Vi bringer dine <span className="text-[#deecd4]">idéer</span> <br />til virkelighed
                  </>
                )}
              </h1>

              <div className="flex items-center gap-1.5 pt-0.5">
                <div className="h-1.5 w-12 rounded-full bg-[#deecd4]" />
                <div className="h-1.5 w-3 rounded-full bg-white/40" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#deecd4]" />
              </div>

              {/* Dynamisk Tekst / Underoverskrift fra CMS */}
              {page?.underoverskrift ? (
                <p className="text-lg text-emerald-50/90 leading-relaxed max-w-2xl">
                  {page.underoverskrift}
                </p>
              ) : page?.indhold ? (
                <div
                  className="text-lg text-emerald-50/90 leading-relaxed max-w-2xl [&_p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: page.indhold }}
                />
              ) : (
                <p className="text-lg text-emerald-50/90 leading-relaxed max-w-2xl">
                  Med 15 års erfaring løser vi dine opgaver inden for skilte, interiørfolie, bildekoration, solfilm til maskiner og biler samt logo design. Vi leverer altid personlig rådgivning og kompromisløs kvalitet.
                </p>
              )}

              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#deecd4] px-6 py-3.5 text-sm font-bold text-[#1b453f] shadow-md transition-all hover:bg-white hover:text-gray-900 cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-xs text-[#1b453f]" />
                  Kontakt os for et tilbud
                </Link>
                <a
                  href="https://www.google.com/maps/dir//''/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x464b45024e9491d5:0x36f4b91c482f1678!3e0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/60 bg-white/15 backdrop-blur-xs px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white hover:text-gray-900 cursor-pointer group"
                >
                  <i className="fa-solid fa-route text-[#deecd4] group-hover:text-gray-900 transition-colors" />
                  Rutevejledning
                </a>
              </div>

              {/* USP mini-liste (Dynamisk fra CMS 'sider' / forside) */}
              <div className="pt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 border-t border-white/15 text-xs font-semibold text-emerald-50/80">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-shield-halved text-[#deecd4]" />
                  <span>{page?.hero_usp_1 || 'Op til 10 års garanti'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-clock text-[#deecd4]" />
                  <span>{page?.hero_usp_2 || 'Hurtig montering'}</span>
                </div>
                <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
                  <i className="fa-solid fa-leaf text-[#deecd4]" />
                  <span>{page?.hero_usp_3 || 'Bæredygtig renovering'}</span>
                </div>
              </div>
            </div>

            {/* Højre kolonne: Billede (Køkken / Hero - Dynamisk fra CMS) */}
            <div className="relative lg:col-span-5">
              <div className="aspect-[4/3] sm:aspect-square overflow-hidden rounded-3xl border border-white/20 shadow-2xl bg-white">
                <img
                  src={
                    heroImage.startsWith('https://assets.auxodev.dk/') && !heroImage.includes('/cdn-cgi/image/')
                      ? `https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`
                      : heroImage
                  }
                  alt={heroImageAlt}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Dekorativ badge */}
              <div className="absolute -bottom-5 -left-5 rounded-2xl bg-white p-4 shadow-xl border border-gray-100 hidden sm:flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green-light/40 text-brand-red">
                  <i className="fa-solid fa-award text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Højeste faglighed</p>
                  <p className="text-sm font-bold text-gray-900">15 års erfaring</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Vores Ydelser Grid */}
      <section className="relative bg-gradient-to-b from-[#f9fbfa] via-white to-[#f4f8f7] py-16 sm:py-20 border-y border-gray-200/80 overflow-hidden">
        {/* Subtil dekorativ baggrundsmønster */}
        <div className="absolute inset-0 bg-[radial-gradient(#245b53_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.035] pointer-events-none" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Specialistkompetencer</span>
            <h2 className="text-3xl font-bold sm:text-4xl text-gray-900">
              Hvad kan vi <span className="text-brand-red">hjælpe dig</span> med?
            </h2>
            <div className="flex items-center justify-center gap-1.5 pt-0.5">
              <div className="h-1 w-2 rounded-full bg-brand-green-light" />
              <div className="h-1 w-14 rounded-full bg-brand-red" />
              <div className="h-1 w-3 rounded-full bg-brand-green-light" />
            </div>
            <p className="text-gray-600 text-sm sm:text-base">
              Vi tilbyder skræddersyede folieløsninger og grafisk design til private, håndværkere og virksomheder.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.href}
                className="group flex flex-col rounded-2xl border border-gray-200/80 bg-white overflow-hidden shadow-xs transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:border-brand-red/40 cursor-pointer block no-underline focus:outline-none focus:ring-2 focus:ring-brand-red/50 focus:ring-offset-2"
              >
                <div className="h-1.5 w-full bg-gradient-to-r from-brand-red via-[#2bb89a] to-brand-green-light opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=85,format=auto/${service.image.replace('https://assets.auxodev.dk/', '')}`}
                    alt={service.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-6 flex-1 flex flex-col justify-between space-y-4 bg-gradient-to-b from-white to-[#fafcfb]">
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-green-light/40 text-brand-red text-sm border border-brand-green-light/70 shadow-2xs group-hover:bg-brand-red group-hover:text-white transition-colors duration-300">
                        <i className={service.icon} />
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-brand-red transition-colors">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-red group-hover:text-brand-red-hover transition-colors group-hover:translate-x-1 transform duration-200">
                      Læs mere & se billeder <i className="fa-solid fa-chevron-right text-[10px]" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Seneste Projekter (Aflang kasse med kategori-filter & nyeste billeder først) */}
      <section className="relative bg-gradient-to-b from-[#f4f8f7] via-slate-50 to-[#edf5f3] py-16 sm:py-20 border-y border-gray-200/80 overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-brand-green-light/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-2 max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Billedstrøm & Galleri</span>
              <h2 className="text-3xl font-bold sm:text-4xl text-gray-900">
                Seneste <span className="text-brand-red">projekter</span>
              </h2>
              <div className="flex items-center gap-1.5 pt-0.5">
                <div className="h-1 w-10 rounded-full bg-brand-red" />
                <div className="h-1 w-3 rounded-full bg-brand-green-light" />
                <div className="h-1 w-1.5 rounded-full bg-brand-red" />
              </div>
              <p className="text-sm text-gray-600">
                Udforsk vores seneste udførte opgaver inden for interiørfolie, bildekoration, solfilm og vægfolie. Vælg en kategori for at filtrere.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-red hover:underline self-start sm:self-auto"
            >
              Har du en opgave til os? <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          {/* Den nye aflange kasse-komponent */}
          <ProjectAlbums />
        </div>
      </section>

      {/* 4. Præsentation & Lokation (Mød Eveline & Værksted - redigerbart i CMS) */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-stretch">
          {/* Mød Eveline / Mennesket bag virksomheden */}
          <div className="rounded-3xl bg-gray-950 text-white p-8 sm:p-10 lg:p-12 relative overflow-hidden shadow-xl border border-gray-800 flex flex-col justify-between">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(24,121,107,0.18),transparent_60%)] pointer-events-none" />
            <div className="relative z-10 space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-green-light">
                  {page?.om_eyebrow || 'Mennesket bag virksomheden'}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold mt-1">
                  {page?.om_titel ? (
                    page.om_titel.includes('Eveline') ? (
                      <>
                        {page.om_titel.split('Eveline')[0]}
                        <span className="text-brand-green-light">Eveline</span>
                        {page.om_titel.split('Eveline')[1]}
                      </>
                    ) : (
                      page.om_titel
                    )
                  ) : (
                    <>
                      Mød <span className="text-brand-green-light">Eveline</span> v. Sonsbeek
                    </>
                  )}
                </h2>
                <div className="flex items-center gap-1.5 pt-1.5">
                  <div className="h-1 w-10 rounded-full bg-brand-green-light" />
                  <div className="h-1 w-3 rounded-full bg-[#10e052]" />
                  <div className="h-1 w-1.5 rounded-full bg-brand-green-light" />
                </div>
              </div>

              <div className="space-y-4">
                {page?.om_tekst ? (
                  <div
                    className="space-y-3 text-sm sm:text-base text-gray-300 leading-relaxed [&_p]:mb-2 [&_p:last-child]:text-xs [&_p:last-child]:sm:text-sm [&_p:last-child]:text-gray-400"
                    dangerouslySetInnerHTML={{ __html: page.om_tekst }}
                  />
                ) : (
                  <div className="space-y-3 text-sm sm:text-base text-gray-300 leading-relaxed">
                    <p>
                      Mit navn er Eveline v. Sonsbeek, og jeg har været i skiltefaget, siden jeg var 17 år. For mig er skiltefaget min passion, og jeg går meget op i, at jeg kan være med fra start til slut på alle mine opgaver.
                    </p>
                    <p className="text-xs sm:text-sm text-gray-400">
                      Min store passion ligger i interiørfolie, et marked som jeg har været med i helt fra starten i Danmark.
                    </p>
                  </div>
                )}

                {/* Billede af Eveline - redigerbart i CMS */}
                {omImage && (
                  <div className="aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden rounded-2xl border border-gray-800 shadow-2xl bg-gray-900 mt-3 relative">
                    <img
                      src={
                        omImage.startsWith('https://assets.auxodev.dk/') && !omImage.includes('/cdn-cgi/image/')
                          ? `https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${omImage.replace('https://assets.auxodev.dk/', '')}`
                          : omImage
                      }
                      alt={page?.om_billede_alt || 'Eveline v. Sonsbeek - CRE8IT'}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="relative z-10 pt-6 mt-6 border-t border-gray-800/80">
              <Link
                to={page?.om_knap_link || '/kontakt'}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-red px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-red-hover cursor-pointer shadow-sm"
              >
                <i className="fa-solid fa-comments" />
                {page?.om_knap_tekst || 'Kontakt Eveline direkte'}
              </Link>
            </div>
          </div>

          {/* Værksted & Lokation */}
          <div className="rounded-3xl border border-gray-200/90 bg-gradient-to-br from-white via-[#f9fcfb] to-[#edf6f4] p-8 sm:p-10 lg:p-12 shadow-xs flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 h-32 w-32 bg-brand-green-light/30 rounded-full blur-2xl pointer-events-none" />
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-red">
                  {page?.vaerksted_eyebrow || 'Værksted & Lokation'}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
                  {page?.vaerksted_titel ? (
                    page.vaerksted_titel.includes('Rødding') ? (
                      <>
                        {page.vaerksted_titel.split('Rødding')[0]}
                        <span className="text-brand-red">Rødding</span>
                        {page.vaerksted_titel.split('Rødding')[1]}
                      </>
                    ) : (
                      page.vaerksted_titel
                    )
                  ) : (
                    <>
                      Besøg os i <span className="text-brand-red">Rødding</span>
                    </>
                  )}
                </h2>
                <div className="flex items-center gap-1.5 pt-1.5">
                  <div className="h-1 w-10 rounded-full bg-brand-red" />
                  <div className="h-1 w-3 rounded-full bg-brand-green-light" />
                  <div className="h-1 w-1.5 rounded-full bg-brand-red" />
                </div>
              </div>

              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                {page?.vaerksted_tekst || (
                  <>
                    Vores værksted er placeret på <strong>{companyAdresse}</strong>. Du er altid velkommen til at kontakte os eller kigge forbi.
                  </>
                )}
              </p>

              <div className="space-y-2.5 text-sm text-gray-700">
                <p className="flex items-center gap-2.5">
                  <i className="fa-solid fa-location-dot text-brand-red w-4 text-center" />
                  <span>{companyAdresse}</span>
                </p>
                <p className="flex items-center gap-2.5">
                  <i className="fa-solid fa-phone text-brand-red w-4 text-center" />
                  <a href={`tel:${companyPhone.replace(/\s+/g, '')}`} className="hover:text-brand-red font-semibold transition-colors whitespace-nowrap">
                    {companyPhone}
                  </a>
                </p>
                <p className="flex items-center gap-2.5">
                  <i className="fa-solid fa-envelope text-brand-red w-4 text-center" />
                  <a
                    href={`mailto:${companyEmail}`}
                    className="hover:text-brand-red font-semibold transition-colors whitespace-nowrap"
                  >
                    {companyEmail}
                  </a>
                </p>
              </div>

              {/* Interaktivt kort */}
              <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl border border-gray-200 shadow-sm bg-gray-100 mt-3">
                <iframe
                  title="CRE8IT Rødding"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2265.0433230674514!2d9.052796977284483!3d55.37581277303117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464b45024e9491d5%3A0x36f4b91c482f1678!2sNordre%20Industrivej%202a%2C%206630%20R%C3%B8dding!5e0!3m2!1sda!2sdk!4v1720435800000!5m2!1sda!2sdk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <div className="pt-6 mt-6 border-t border-gray-100">
              <a
                href="https://www.google.com/maps/dir//''/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x464b45024e9491d5:0x36f4b91c482f1678!3e0"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-bold text-brand-red hover:underline"
              >
                <span>Få rutevejledning på Google Maps</span> &rarr;
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
